import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BackLink } from "@/components/BackLink";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Trophy, Plus, Loader2, CalendarClock, Users, ListChecks } from "lucide-react";
import { MatchDaySquad } from "@/components/MatchDaySquad";
import { FixtureImport } from "@/components/FixtureImport";
import { FixtureResult } from "@/components/FixtureResult";
import { LeagueTable } from "@/components/LeagueTable";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLE = {
  scheduled: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  postponed: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  cancelled: "bg-red-500/15 text-red-300 border-red-500/40",
  played: "bg-slate-600/30 text-slate-300 border-slate-600",
};

export default function LeagueFixtures({ user }) {
  const isAdmin = ["convenor", "captain", "academy_admin"].includes(user?.role);
  const [teams, setTeams] = useState({ league: [], training: [] });
  const [scope, setScope] = useState("assigned");
  const [fixtures, setFixtures] = useState([]);
  const [teamId, setTeamId] = useState("");
  const [form, setForm] = useState({ date: "", start_time: "", opponent: "", venue: "", home_away: "home", fixture_type: "league", notes: "" });
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [squadFor, setSquadFor] = useState("");
  const [resultFor, setResultFor] = useState("");
  const [detecting, setDetecting] = useState(false);
  const [legacyGames, setLegacyGames] = useState(null);
  const [moving, setMoving] = useState(false);

  const allTeams = [...teams.league, ...teams.training];
  const selectedTeam = allTeams.find((t) => t.id === teamId);
  const selectedIsLeague = selectedTeam?.team_type === "league";
  const selectedLeagueName = selectedTeam?.league_name || "";

  const loadTeams = async () => {
    try {
      const r = await axios.get(`${API}/academy/teams-by-type`, { withCredentials: true });
      setTeams({ league: r.data.league || [], training: r.data.training || [] });
      setScope(r.data.scope || "assigned");
      if (!teamId) {
        const first = (r.data.league || [])[0] || (r.data.training || [])[0];
        if (first) setTeamId(first.id);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load teams");
    }
  };

  const loadFixtures = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/academy/fixtures`, {
        params: { upcoming_only: false }, withCredentials: true,
      });
      setFixtures(r.data.fixtures || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load fixtures");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTeams();
    loadFixtures();
    if (isAdmin) checkLegacyGames(); // admin-only endpoint: don't 403 at coaches
  }, [isAdmin]); // eslint-disable-line

  const checkLegacyGames = async () => {
    try {
      const r = await axios.post(`${API}/academy/admin/fixtures/import-session-games`,
        { dry_run: true }, { withCredentials: true });
      setLegacyGames(r.data.total ? r.data : null);
    } catch {
      setLegacyGames(null); // coaches aren't admins; nothing to show them
    }
  };

  const moveLegacyGames = async () => {
    setMoving(true);
    try {
      const r = await axios.post(`${API}/academy/admin/fixtures/import-session-games`, {},
        { withCredentials: true });
      toast.success(`${r.data.total} game(s) moved here from the Sessions page`);
      setLegacyGames(null);
      loadFixtures();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not move them");
    } finally {
      setMoving(false);
    }
  };

  const addFixture = async () => {
    if (!teamId) { toast.error("Pick the team"); return; }
    if (!form.date || !form.opponent) { toast.error("Date and opponent are needed"); return; }
    setBusy(true);
    try {
      const body = { ...form, fixture_type: selectedIsLeague ? form.fixture_type : "friendly" };
      const r = await axios.post(`${API}/academy/teams/${teamId}/fixtures`, body, { withCredentials: true });
      const wa = r.data.whatsapp || {};
      const waNote = wa.sent
        ? ` · WhatsApp sent to ${wa.sent}, asking yes/no`
        : wa.skipped === "whatsapp_disabled"
          ? " · WhatsApp is switched off, so no yes/no was asked"
          : wa.skipped
            ? " · WhatsApp not sent"
            : "";
      toast.success(`Game added · ${r.data.parents_notified} parent(s) told${waNote}`);
      setForm({ date: "", start_time: "", opponent: "", venue: "", home_away: "home", fixture_type: "league", notes: "" });
      loadFixtures();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add the game");
    } finally {
      setBusy(false);
    }
  };

  const detectLeagues = async () => {    setDetecting(true);
    try {
      const r = await axios.post(`${API}/academy/admin/teams/detect-leagues`, {},
        { withCredentials: true });
      const n = (r.data.updated || []).length;
      toast.success(n
        ? `${n} team(s) set up as league sides: ${r.data.updated.map((t) => t.name).join(", ")}`
        : "Every JSSL and SYL team is already set up");
      loadTeams();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not set the teams up");
    } finally {
      setDetecting(false);
    }
  };

  const patchFixture = async (fx, payload, label) => {
    if (label && !window.confirm(`${label} — ${fx.team_name} vs ${fx.opponent} on ${fx.label}? Parents will be told.`)) return;
    setBusy(true);
    try {
      const r = await axios.patch(`${API}/academy/fixtures/${fx.id}`, payload, { withCredentials: true });
      toast.success(`Updated · ${r.data.parents_notified} parent(s) told`);
      loadFixtures();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update the fixture");
    } finally {
      setBusy(false);
    }
  };

  const markLeague = async (team, type) => {
    const leagueName = type === "league"
      ? window.prompt("Which league? (e.g. JSSL, SYL)", team.league_name || "")
      : null;
    if (type === "league" && leagueName === null) return;
    try {
      await axios.post(`${API}/academy/admin/teams/${team.id}/team-type`,
        { team_type: type, league_name: leagueName }, { withCredentials: true });
      toast.success(`${team.name} is now a ${type} team`);
      loadTeams();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Only admins can change the team type");
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <div className="max-w-4xl mx-auto px-4 py-6 sm:px-6 sm:py-8 space-y-6">
        <BackLink to="/academy/coach-dashboard" label="Back"
          className="text-slate-300 hover:text-white -ml-2"
          testId="fixtures-back" />

        <header>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <Trophy className="w-6 h-6 text-amber-400" /> Games
          </h1>
          <p className="text-sm text-slate-400 mt-2">
            <strong className="text-slate-200">Every game lives here</strong> — JSSL, SYL and
            friendlies alike. Parents of that squad are told straight away by notification, email
            and WhatsApp, and the WhatsApp asks them to reply YES or NO so availability lands in
            the portal. Games aren&apos;t charged as training sessions, so a postponement never
            creates a credit.
          </p>
        </header>

        {legacyGames && (
          <Card className="bg-amber-500/5 border-amber-500/30" data-testid="legacy-games-card">
            <CardContent className="py-4">
              <p className="text-sm text-amber-200">
                <strong>{legacyGames.total} game(s)</strong> were created on the old Sessions page
                and still live there. Move them here so every game is in one place.
              </p>
              <p className="text-xs text-slate-400 mt-1">
                {(legacyGames.moved || []).concat(legacyGames.kept_for_attendance || [])
                  .map((g) => `${g.team_name} vs ${g.opponent} (${g.date})`).join(" · ")}
              </p>
              <Button size="sm" onClick={moveLegacyGames} disabled={moving}
                className="mt-3 h-9 text-[11px] bg-amber-600 hover:bg-amber-500"
                data-testid="move-legacy-games-btn">
                {moving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                Move them into Games
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Add a fixture */}
        <Card className="bg-slate-900/60 border-slate-800">          <CardHeader className="pb-3">
            <CardTitle className="text-base">Add a game date</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {allTeams.length === 0 ? (
              <p className="text-sm text-amber-300" data-testid="fixtures-no-league-teams">
                No teams to add a game for yet.
              </p>
            ) : (
              <>
                <div className="grid gap-2 sm:grid-cols-2">
                  <Select value={teamId} onValueChange={setTeamId}>
                    <SelectTrigger className="h-11" data-testid="fixture-team-select">
                      <SelectValue placeholder="Team" />
                    </SelectTrigger>
                    <SelectContent>
                      {allTeams.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.name}
                          {t.league_name ? ` · ${t.league_name}` : " · friendly only"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input type="date" className="h-11" value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="fixture-date" />
                  <Input className="h-11" placeholder="Opponent" value={form.opponent}
                    onChange={(e) => setForm({ ...form, opponent: e.target.value })} data-testid="fixture-opponent" />
                  <Input type="time" className="h-11" value={form.start_time}
                    onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="fixture-time" />
                  <Input className="h-11" placeholder="Venue" value={form.venue}
                    onChange={(e) => setForm({ ...form, venue: e.target.value })} data-testid="fixture-venue" />
                  <Select value={form.home_away} onValueChange={(v) => setForm({ ...form, home_away: v })}>
                    <SelectTrigger className="h-11" data-testid="fixture-home-away">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="home">Home</SelectItem>
                      <SelectItem value="away">Away</SelectItem>
                      <SelectItem value="neutral">Neutral</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select
                    value={selectedIsLeague ? form.fixture_type : "friendly"}
                    onValueChange={(v) => setForm({ ...form, fixture_type: v })}
                    disabled={!selectedIsLeague}
                  >
                    <SelectTrigger className="h-11" data-testid="fixture-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="league">
                        League game{selectedLeagueName ? ` · ${selectedLeagueName}` : ""}
                      </SelectItem>
                      <SelectItem value="friendly">Friendly / in-house</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {!selectedIsLeague && (
                  <p className="text-xs text-slate-500" data-testid="fixture-friendly-only-note">
                    This team isn&apos;t set up as a league side, so this will be recorded as a
                    friendly — it won&apos;t count towards a league record.
                  </p>
                )}
                <Button onClick={addFixture} disabled={busy}
                  className="h-11 w-full sm:w-auto bg-amber-600 hover:bg-amber-500" data-testid="fixture-add-btn">
                  {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                  Add game, tell parents &amp; ask yes/no
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        {/* Bulk import */}
        {teams.league.length > 0 && (
          <LeagueTable refreshKey={fixtures.length} />
        )}
        {teams.league.length > 0 && (
          <FixtureImport
            teamId={teamId}
            teamName={teams.league.find((t) => t.id === teamId)?.name}
            onImported={loadFixtures}
          />
        )}

        {/* Fixture list */}
        <Card className="bg-slate-900/60 border-slate-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-amber-400" /> Fixtures
              <span className="text-xs font-normal text-slate-500">{fixtures.length}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {loading && <p className="text-sm text-slate-400">Loading…</p>}
            {!loading && fixtures.length === 0 && (
              <p className="text-sm text-slate-500" data-testid="fixtures-empty">No fixtures yet.</p>
            )}
            {fixtures.map((fx) => (
              <div key={fx.id} className="rounded-lg bg-slate-950/60 border border-slate-800 px-3 py-2"
                data-testid={`fixture-row-${fx.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm text-white">
                      {fx.team_name} <span className="text-slate-500">vs</span> {fx.opponent}
                    </p>
                    <p className="text-xs text-slate-400">
                      {[fx.label, fx.start_time, fx.venue, fx.home_away].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {fx.fixture_type === "friendly" ? (
                      <Badge className="bg-slate-700/40 text-slate-300 border-slate-600 text-[10px]"
                        data-testid={`fixture-type-${fx.id}`}>friendly</Badge>
                    ) : (
                      <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/40 text-[10px]"
                        data-testid={`fixture-type-${fx.id}`}>
                        {fx.league_name || "league"}
                      </Badge>
                    )}
                    <Badge className={`${STATUS_STYLE[fx.status] || STATUS_STYLE.scheduled} text-[10px]`}
                      data-testid={`fixture-status-${fx.id}`}>
                      {fx.status}
                    </Badge>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {fx.status !== "postponed" && (
                    <Button size="sm" variant="outline" className="h-9 text-[11px] border-amber-500/40 text-amber-200"
                      disabled={busy}
                      onClick={() => patchFixture(fx, { status: "postponed" }, "Postpone")}
                      data-testid={`fixture-postpone-${fx.id}`}>Postpone</Button>
                  )}
                  {fx.status !== "cancelled" && (
                    <Button size="sm" variant="outline" className="h-9 text-[11px] border-red-500/40 text-red-200"
                      disabled={busy}
                      onClick={() => patchFixture(fx, { status: "cancelled" }, "Cancel")}
                      data-testid={`fixture-cancel-${fx.id}`}>Cancel</Button>
                  )}
                  <Button size="sm" variant="outline" className="h-9 text-[11px]"
                    disabled={busy}
                    onClick={() => {
                      const d = window.prompt("New date (YYYY-MM-DD)", fx.date);
                      if (d) patchFixture(fx, { date: d, status: "scheduled" }, null);
                    }}
                    data-testid={`fixture-reschedule-${fx.id}`}>New date</Button>
                  <Button size="sm" variant="outline"
                    className="h-9 text-[11px] border-sky-500/40 text-sky-200"
                    onClick={() => setSquadFor(squadFor === fx.id ? "" : fx.id)}
                    data-testid={`fixture-squad-${fx.id}`}>
                    <ListChecks className="w-3 h-3 mr-1" />
                    Squad{fx.squad_count ? ` (${fx.squad_count})` : ""}
                    {fx.squad_shared_at ? " ✓" : ""}
                  </Button>
                  {fx.status !== "scheduled" && (
                    <Button size="sm" variant="outline" className="h-9 text-[11px] border-emerald-500/40 text-emerald-200"
                      disabled={busy}
                      onClick={() => patchFixture(fx, { status: "scheduled" }, "Put back on")}
                      data-testid={`fixture-restore-${fx.id}`}>Back on</Button>
                  )}
                  <Button size="sm" variant="outline"
                    className="h-9 text-[11px] border-emerald-500/40 text-emerald-200"
                    onClick={() => setResultFor(resultFor === fx.id ? "" : fx.id)}
                    data-testid={`fixture-result-${fx.id}`}>
                    {fx.result ? `${fx.result.our_score}–${fx.result.their_score}` : "Score"}
                  </Button>
                </div>
                {squadFor === fx.id && (
                  <MatchDaySquad fixtureId={fx.id} onClose={() => setSquadFor("")} onSaved={loadFixtures} />
                )}
                {resultFor === fx.id && (
                  <FixtureResult fixture={fx} onSaved={() => { setResultFor(""); loadFixtures(); }} />
                )}
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Team types */}
        <Card className="bg-slate-900/60 border-slate-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-5 h-5 text-emerald-400" /> League vs training teams
            </CardTitle>
            <p className="text-xs text-slate-500">
              League sides play weekend fixtures. Training groups follow the term timetable and session fees.
              {scope !== "all" && " You're seeing your own teams only."}
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            {scope === "all" && (
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3">
                <p className="text-xs text-slate-300">
                  Teams named after their league — <strong>JSSL B12</strong>, <strong>SYL B12</strong> —
                  can be set up in one go.
                </p>
                <Button size="sm" onClick={detectLeagues} disabled={detecting}
                  className="mt-2 h-9 text-[11px] bg-emerald-600 hover:bg-emerald-500"
                  data-testid="detect-leagues-btn">
                  {detecting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                  Set up JSSL &amp; SYL teams from their names
                </Button>
              </div>
            )}
            <div>
              <p className="text-xs uppercase tracking-wide text-amber-300 mb-1">League</p>
              {teams.league.length === 0 && <p className="text-sm text-slate-500">None yet.</p>}
              {teams.league.map((t) => (
                <div key={t.id} className="flex items-center justify-between gap-2 py-1" data-testid={`league-team-${t.id}`}>
                  <span className="text-sm">
                    {t.name}
                    <span className="text-xs text-slate-500">
                      {t.league_name ? ` · ${t.league_name}` : ""} · {t.player_count} player(s)
                      {t.fixture_count ? ` · ${t.fixture_count} fixture(s)` : ""}
                    </span>
                  </span>
                  <Button size="sm" variant="ghost" className="text-[11px] text-slate-400"
                    onClick={() => markLeague(t, "training")} data-testid={`make-training-${t.id}`}>
                    make training
                  </Button>
                </div>
              ))}
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-emerald-300 mb-1">Training</p>
              {teams.training.map((t) => (
                <div key={t.id} className="flex items-center justify-between gap-2 py-1" data-testid={`training-team-${t.id}`}>
                  <span className="text-sm">
                    {t.name}
                    <span className="text-xs text-slate-500">
                      {t.is_bucket ? " · bucket" : ""} · {t.player_count} player(s)
                    </span>
                  </span>
                  <Button size="sm" variant="ghost" className="text-[11px] text-amber-300"
                    onClick={() => markLeague(t, "league")} data-testid={`make-league-${t.id}`}>
                    mark as league
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
